import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { DetectedDateDetail, DetectedDatesSummary } from '../../../models/home';
import { LanguageService } from '../../../services/language/language.service';
import { HomeInteractionStateService } from "../../../services/home-ia/home-interaction-state.service";
import { UxError } from '../../../models/ux-error.model';

@Component({
  selector: 'app-home-dates-radar',
  templateUrl: './home-dates-radar.component.html',
  styleUrls: [
    './home-dates-radar.component.shell-and-filters.scss',
    './home-dates-radar.component.date-items.scss',
    './home-dates-radar.component.tooltips-and-responsive.scss'
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeDatesRadarComponent {
  @Input() summary: DetectedDatesSummary | null = null;
  @Input() loading = false;
  @Input() error: UxError | null = null;
  @Input() empty = false;
  @Input() filterSaving = false;
  @Input() filterMessageKey: string | null = null;
  @Input() filterError: UxError | null = null;
  @Output() retryRequest = new EventEmitter<void>();
  @Output() roleFilterChange = new EventEmitter<string | null>();

  readonly selectedRole$ = this.interactionState.selectedRole$;
  readonly datesPanelOpen$ = this.interactionState.datesPanelOpen$;

  constructor(
    public lang: LanguageService,
    private readonly interactionState: HomeInteractionStateService
  ) {}

  get roleEntries(): Array<{ role: string; count: number }> {
    if (!this.summary?.byRole) {
      return [];
    }
    return Object.entries(this.summary.byRole)
      .map(([role, count]) => ({ role, count }))
      .filter((entry) => this.translateRole(entry.role));
  }

  translateRole(role: string): string {
    const label = this.summary?.roleLabels?.[role];
    if (label) {
      return label;
    }
    const key = `home.datesSummary.role.${role}`;
    const translated = this.lang.translate(key);
    return translated === key ? '' : translated;
  }

  translateState(state: string, label?: string | null): string {
    if (label) {
      return label;
    }
    const key = `home.datesSummary.state.${state}`;
    const translated = this.lang.translate(key);
    return translated === key ? state : translated;
  }

  get monitoredDates(): readonly DetectedDateDetail[] {
    return this.summary?.monitoredDates ?? [];
  }

  get previewDates(): readonly DetectedDateDetail[] {
    return this.monitoredDates.slice(0, 3);
  }

  onViewDatesPanel(): void {
    this.interactionState.toggleDatesPanel(true);
  }

  onRetry(): void {
    this.retryRequest.emit();
  }

  onCloseDatesPanel(): void {
    this.interactionState.toggleDatesPanel(false);
  }

  onChangeRole(): void {
    this.onViewDatesPanel();
  }

  onOpenCalendar(): void {
    this.onViewDatesPanel();
  }

  onRoleToggle(role: string): void {
    const current = this.interactionState.selectedRoleSnapshot;
    const nextRole = current === role ? null : role;
    this.interactionState.selectRole(nextRole);
    this.roleFilterChange.emit(nextRole);
  }

  clearRoleFilter(): void {
    this.interactionState.selectRole(null);
    this.roleFilterChange.emit(null);
  }

  onPanelInteraction(event: MouseEvent): void {
    event.stopPropagation();
  }

  trackDate(_: number, date: DetectedDateDetail): string {
    return date.id;
  }
}
